import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Car, Home, Briefcase, Heart, Plane, Ship, HelpCircle, Building2, User, ChevronLeft, ArrowRight, Phone, Mail, Wrench, Shield, FileText, Tractor, Bike, Users, RefreshCw } from 'lucide-react';
import { QUOTE_LINKS } from '@/data/quoteLinks';
import { TravelQuoteOnline } from '@/components/TravelQuoteOnline';

// Analytics helper - fires GA4 events with required parameters
const trackEvent = (eventName, params = {}) => {
  const eventParams = {
    source_page: window.location.pathname,
    ...params
  };
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
  console.log(`[Analytics] ${eventName}`, eventParams);
};

const QuoteModal = ({ open, onOpenChange, preselectedCategory = null, sourcePage = null, contextHint = null }) => {
  const [step, setStep] = useState('categories');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState('Property & Liability');

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Determine context-aware default accordion group for business
  const getDefaultAccordionGroup = useCallback(() => {
    const path = sourcePage || window.location.pathname;
    
    // Contractor pages
    if (path.includes('general-contractor') || path.includes('painting-contractors') || path.includes('electrical-contractor') ||
        path.includes('plumbing') || path.includes('roofing') || 
        path.includes('janitorial') || path.includes('farm')) {
      return 'Contractors';
    }
    
    // Professional & Specialty pages
    if (path.includes('cyber') || path.includes('errors-omissions') || 
        path.includes('directors-officers')) {
      return 'Professional & Specialty Coverage';
    }
    
    return 'Property & Liability';
  }, [sourcePage]);

  // Reset state when modal opens - this is intentional synchronization with props
  useEffect(() => {
    if (open) {
      const currentSource = sourcePage || window.location.pathname;
      trackEvent('quote_modal_opened', { 
        category: preselectedCategory || 'top_level',
        source_page: currentSource
      });
      
      if (preselectedCategory) {
        setSelectedCategory(preselectedCategory);
        setStep(preselectedCategory);
      } else {
        setStep('categories');
        setSelectedCategory(null);
      }
      
      // Set context-aware default accordion group
      setExpandedAccordion(getDefaultAccordionGroup());
    }
  }, [open, preselectedCategory, sourcePage, getDefaultAccordionGroup]);

  const redirectToForm = useCallback((url, optionName, category, subCategory = null) => {
    trackEvent('quote_redirect', { 
      category: category || selectedCategory,
      sub_category: subCategory,
      option: optionName.toLowerCase().replace(/\s+/g, '_'),
      exit_type: 'formstack',
      url
    });
    trackEvent('quote_exit_type', { 
      category: category || selectedCategory,
      option: optionName.toLowerCase().replace(/\s+/g, '_'),
      exit_type: 'formstack'
    });
    window.open(url, '_blank');
    onOpenChange(false);
  }, [onOpenChange, selectedCategory]);

  const handleCategorySelect = (category, isPreselected = false) => {
    setSelectedCategory(category);
    if (!isPreselected) {
      trackEvent('quote_category_selected', { category });
    }
    setStep(category);
  };

  const handleOptionSelect = (optionName, url, category, subCategory = null) => {
    trackEvent('quote_option_selected', { 
      category: category || selectedCategory, 
      sub_category: subCategory,
      option: optionName.toLowerCase().replace(/\s+/g, '_')
    });
    if (url) {
      redirectToForm(url, optionName, category, subCategory);
    }
  };

  const handleTalkToUs = (action, category, option) => {
    trackEvent('quote_exit_type', { 
      category: category || selectedCategory,
      option: option ? option.toLowerCase().replace(/\s+/g, '_') : 'general',
      exit_type: action
    });
  };

  const handleBack = () => {
    setStep('categories');
    setSelectedCategory(null);
    setExpandedAccordion(getDefaultAccordionGroup());
  };

  // Category cards for Step 1
  const categories = [
    { id: 'auto', label: 'Auto Insurance', description: 'ICBC renewals and optional coverage', icon: Car },
    { id: 'home', label: 'Home Insurance', description: 'House, condo, tenant, and landlord coverage', icon: Home },
    { id: 'business', label: 'Business Insurance', description: 'Liability, property, and contractor coverage', icon: Briefcase },
    { id: 'life', label: 'Life & Benefits', description: 'Life insurance and group benefits', icon: Heart },
    { id: 'travel', label: 'Travel Insurance', description: 'Emergency medical and trip coverage', icon: Plane },
    { id: 'marine', label: 'Marine Insurance', description: 'Boat and watercraft protection', icon: Ship },
    { id: 'notSure', label: 'Not sure?', description: 'We can help you figure it out', icon: HelpCircle }
  ];

  // Auto Insurance options - NO AUTO-REDIRECT
  const autoOptions = [
    { id: 'icbcRenewal', label: 'ICBC Renewal', icon: RefreshCw, url: QUOTE_LINKS.auto, description: 'Renew your ICBC insurance' },
    { id: 'replacementCost', label: 'Replacement Cost Coverage', icon: Car, url: null, talkToUs: true, description: 'Protect your vehicle\'s value' },
    { id: 'fleet', label: 'Fleet Insurance', icon: Car, url: null, talkToUs: true, description: 'Coverage for multiple vehicles' },
    { id: 'motorcycle', label: 'Motorcycle Insurance', icon: Bike, url: null, talkToUs: true, description: 'Protection for your bike' },
    { id: 'newToBc', label: 'New to BC', icon: Users, url: null, talkToUs: true, description: 'Just moved to British Columbia?' }
  ];

  // Home Insurance options
  const homeOptions = [
    { id: 'homeowners', label: 'Homeowners', icon: Home, url: QUOTE_LINKS.home.homeowners },
    { id: 'condo', label: 'Condo', icon: Building2, url: QUOTE_LINKS.home.condo },
    { id: 'tenants', label: 'Tenants', icon: User, url: QUOTE_LINKS.home.tenants },
    { id: 'landlordHouse', label: 'Landlord House', icon: Home, url: QUOTE_LINKS.home.landlordHouse },
    { id: 'landlordCondo', label: 'Landlord Condo', icon: Building2, url: QUOTE_LINKS.home.landlordCondo },
    { id: 'strata', label: 'Strata', icon: Building2, url: QUOTE_LINKS.home.strata },
    { id: 'newConstruction', label: 'New Construction', icon: Wrench, url: QUOTE_LINKS.home.newConstruction },
    { id: 'seasonalDwelling', label: 'Seasonal Dwelling', icon: Home, url: QUOTE_LINKS.home.seasonalDwelling },
    { id: 'vacantDwelling', label: 'Vacant Dwelling', icon: Home, url: QUOTE_LINKS.home.vacantDwelling },
    { id: 'vacantLand', label: 'Vacant Land', icon: FileText, url: QUOTE_LINKS.home.vacantLand }
  ];

  // Business Insurance - LOCKED grouping with FINAL names
  const businessGroups = {
    'Property & Liability': [
      { id: 'businessProperty', label: 'Business Property', icon: Building2, url: QUOTE_LINKS.business.businessProperty },
      { id: 'commercialLiability', label: 'Commercial General Liability', icon: Shield, url: QUOTE_LINKS.business.commercialLiability },
      { id: 'commercialStrata', label: 'Commercial Strata', icon: Building2, url: QUOTE_LINKS.business.commercialStrata }
    ],
    'Contractors': [
      { id: 'generalContractors', label: 'General Contractors', icon: Wrench, url: QUOTE_LINKS.business.generalContractors },
      { id: 'paintingContractors', label: 'Painting Contractors', icon: Wrench, url: QUOTE_LINKS.business.paintingContractors, talkToUs: !QUOTE_LINKS.business.paintingContractors },
      { id: 'electricalContractors', label: 'Electrical Contractors', icon: Wrench, url: QUOTE_LINKS.business.electricalContractors },
      { id: 'plumbingHeating', label: 'Plumbing & Heating', icon: Wrench, url: QUOTE_LINKS.business.plumbingHeating },
      { id: 'roofingContractors', label: 'Roofing Contractors', icon: Wrench, url: QUOTE_LINKS.business.roofingContractors },
      { id: 'janitorialCleaning', label: 'Janitorial & Cleaning', icon: Wrench, url: QUOTE_LINKS.business.janitorialCleaning },
      { id: 'farm', label: 'Farm Insurance', icon: Tractor, url: QUOTE_LINKS.business.farm }
    ],
    'Professional & Specialty Coverage': [
      { id: 'cyber', label: 'Cyber Insurance', icon: Shield, url: null, talkToUs: true },
      { id: 'errorsOmissions', label: 'Errors & Omissions', icon: FileText, url: null, talkToUs: true },
      { id: 'directorsOfficers', label: 'Directors & Officers', icon: User, url: null, talkToUs: true }
    ]
  };

  // Life & Benefits options
  const lifeOptions = [
    { id: 'lifeInsurance', label: 'Life Insurance', icon: Heart, url: QUOTE_LINKS.life.lifeInsurance },
    { id: 'groupBenefits', label: 'Group Benefits', icon: Users, url: QUOTE_LINKS.life.groupBenefits }
  ];

  // Marine options
  const marineOptions = [
    { id: 'boatInsurance', label: 'Boat Insurance', icon: Ship, url: QUOTE_LINKS.marine.boatInsurance }
  ];

  // Render option button
  const OptionButton = ({ option, onClick, showDescription = false }) => {
    const Icon = option.icon;
    return (
      <button
        onClick={onClick}
        className="group flex items-center gap-3 p-3 sm:p-4 rounded-xl border border-neutral-200 bg-white 
          hover:border-cta hover:shadow-md transition-all duration-200 text-left w-full"
        data-testid={`quote-option-${option.id}`}
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-soft flex items-center justify-center
          group-hover:bg-cta transition-colors duration-200">
          <Icon className="h-5 w-5 text-cta group-hover:text-white transition-colors duration-200" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="font-medium text-[#111827] group-hover:text-cta transition-colors text-sm sm:text-base block"
            >
            {option.label}
          </span>
          {showDescription && option.description && (
            <span className="text-xs text-neutral-500 mt-0.5 block" >
              {option.description}
            </span>
          )}
        </div>
        <ArrowRight className="h-4 w-4 text-neutral-300 group-hover:text-cta
          group-hover:translate-x-1 transition-all duration-200 flex-shrink-0" />
      </button>
    );
  };

  // Standardized Talk-to-Us mini modal content
  const TalkToUsContent = ({ category, option }) => (
    <div className="space-y-4 py-4">
      <p className="text-sm text-neutral-600 text-center mb-6" >
        This coverage requires a quick conversation. Let&apos;s help you find the right solution.
      </p>
      <div className="grid gap-3">
        <Button 
          className="w-full bg-cta hover:bg-cta-hover" 
          asChild
          onClick={() => handleTalkToUs('call_us', category, option)}
        >
          <a href="tel:604-298-1218">
            <Phone className="h-4 w-4 mr-2" />
            Call Us
          </a>
        </Button>
        <Button 
          variant="outline" 
          className="w-full border-neutral-300" 
          asChild
          onClick={() => handleTalkToUs('email_us', category, option)}
        >
          <a href="mailto:info@jfi.bc.ca">
            <Mail className="h-4 w-4 mr-2" />
            Email Us
          </a>
        </Button>
      </div>
    </div>
  );

  // Not Sure content
  const NotSureContent = () => (
    <div className="space-y-4 py-4">
      <p className="text-sm text-neutral-600 text-center mb-6" >
        No problem! We&apos;re here to help you figure out the right coverage.
      </p>
      <div className="grid gap-3">
        <Button 
          className="w-full bg-cta hover:bg-cta-hover" 
          onClick={() => { setStep('categories'); setSelectedCategory(null); }}
        >
          Browse Categories
        </Button>
        <Button 
          variant="outline" 
          className="w-full border-neutral-300" 
          asChild
          onClick={() => handleTalkToUs('call_us', 'not_sure', null)}
        >
          <a href="tel:604-298-1218">
            <Phone className="h-4 w-4 mr-2" />
            Call Us
          </a>
        </Button>
        <Button 
          variant="outline" 
          className="w-full border-neutral-300" 
          asChild
          onClick={() => handleTalkToUs('email_us', 'not_sure', null)}
        >
          <a href="mailto:info@jfi.bc.ca">
            <Mail className="h-4 w-4 mr-2" />
            Email Us
          </a>
        </Button>
      </div>
    </div>
  );

  // Get title based on step
  const getTitle = () => {
    switch (step) {
      case 'categories': return 'Get a Quote';
      case 'auto': return 'Auto Insurance';
      case 'home': return 'Home Insurance';
      case 'business': return 'Business Insurance';
      case 'life': return 'Life & Benefits';
      case 'travel': return 'Travel Insurance';
      case 'marine': return 'Marine Insurance';
      case 'notSure': return 'Not Sure?';
      case 'talkToUs': return 'Let\'s Talk';
      default: return 'Get a Quote';
    }
  };

  const getDescription = () => {
    switch (step) {
      case 'categories': return 'Select an insurance category to get started';
      case 'auto': return 'Select your auto insurance need';
      case 'home': return 'Choose your property type';
      case 'business': return 'Select your business coverage type';
      case 'life': return 'Choose your coverage';
      case 'travel': return 'We\'ll help you find the right travel coverage';
      case 'marine': return 'Choose your watercraft coverage';
      case 'notSure': return 'We\'re here to help';
      case 'talkToUs': return 'This coverage requires a conversation';
      default: return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={`p-0 gap-0 overflow-hidden
          ${isMobile 
            ? 'fixed bottom-0 left-0 right-0 top-auto translate-x-0 translate-y-0 rounded-t-2xl rounded-b-none max-w-none w-full max-h-[90vh]' 
            : 'sm:max-w-[600px]'
          }`}
        data-testid="quote-modal"
      >
        <DialogHeader className="p-4 sm:p-6 pb-3 sm:pb-4 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            {step !== 'categories' && (
              <button 
                onClick={handleBack}
                className="p-1 -ml-1 rounded-full hover:bg-neutral-100 transition-colors"
                aria-label="Go back"
                data-testid="quote-modal-back"
              >
                <ChevronLeft className="h-5 w-5 text-neutral-500" />
              </button>
            )}
            <div>
              <DialogTitle 
                className="text-lg sm:text-xl font-semibold text-[#111827]"
                             >
                {getTitle()}
              </DialogTitle>
              <DialogDescription 
                className="text-xs sm:text-sm text-neutral-600 mt-0.5"
                             >
                {getDescription()}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh] sm:max-h-[500px]">
          {/* Step 1: Categories */}
          {step === 'categories' && (
            <div className="grid gap-2 sm:gap-3">
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat.id)}
                    className="group flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border border-neutral-200 bg-white 
                      hover:border-cta hover:shadow-md transition-all duration-200 text-left"
                    data-testid={`quote-category-${cat.id}`}
                  >
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-brand-soft flex items-center justify-center
                      group-hover:bg-cta transition-colors duration-200">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-cta group-hover:text-white transition-colors duration-200" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#111827] group-hover:text-cta transition-colors text-sm sm:text-base"
                        >
                        {cat.label}
                      </h3>
                      <p className="text-xs sm:text-sm text-neutral-500 mt-0.5"
                        >
                        {cat.description}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-neutral-300 group-hover:text-cta 
                      group-hover:translate-x-1 transition-all duration-200 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Auto Insurance Options - NO AUTO-REDIRECT */}
          {step === 'auto' && (
            <div className="grid gap-2 sm:gap-3">
              {autoOptions.map((option) => (
                <OptionButton 
                  key={option.id} 
                  option={option}
                  showDescription={true}
                  onClick={() => {
                    if (option.talkToUs) {
                      setStep('talkToUs');
                      setSelectedCategory('auto');
                    } else {
                      handleOptionSelect(option.label, option.url, 'auto');
                    }
                  }} 
                />
              ))}
            </div>
          )}

          {/* Home Insurance Options */}
          {step === 'home' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {homeOptions.map((option) => (
                <OptionButton 
                  key={option.id} 
                  option={option} 
                  onClick={() => handleOptionSelect(option.label, option.url, 'home')} 
                />
              ))}
            </div>
          )}

          {/* Business Insurance Options - LOCKED SPEC */}
          {step === 'business' && (
            <>
              {isMobile ? (
                // Mobile: Accordion - ONE group open at a time
                <Accordion 
                  type="single" 
                  collapsible 
                  value={expandedAccordion}
                  onValueChange={setExpandedAccordion}
                  className="space-y-2"
                  data-testid="business-accordion"
                >
                  {Object.entries(businessGroups).map(([groupName, options]) => (
                    <AccordionItem key={groupName} value={groupName} className="border rounded-lg">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline" data-testid={`business-group-${groupName.toLowerCase().replace(/\s+/g, '-')}`}>
                        <span className="font-semibold text-sm" >
                          {groupName}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-3">
                        <div className="grid gap-2">
                          {options.map((option) => (
                            <OptionButton 
                              key={option.id} 
                              option={option} 
                              onClick={() => {
                                if (option.talkToUs) {
                                  setStep('talkToUs');
                                } else {
                                  handleOptionSelect(option.label, option.url, 'business');
                                }
                              }} 
                            />
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                // Desktop: Grouped 2-3 column grid with visible headings
                <div className="space-y-6">
                  {Object.entries(businessGroups).map(([groupName, options]) => (
                    <div key={groupName} data-testid={`business-group-${groupName.toLowerCase().replace(/\s+/g, '-')}`}>
                      <h4 className="font-semibold text-sm text-neutral-700 mb-3 border-b border-neutral-100 pb-2" 
                        >
                        {groupName}
                      </h4>
                      <div className={`grid gap-2 ${options.length <= 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3'}`}>
                        {options.map((option) => (
                          <OptionButton 
                            key={option.id} 
                            option={option} 
                            onClick={() => {
                              if (option.talkToUs) {
                                setStep('talkToUs');
                              } else {
                                handleOptionSelect(option.label, option.url, 'business');
                              }
                            }} 
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Life & Benefits Options */}
          {step === 'life' && (
            <div className="grid gap-3">
              {lifeOptions.map((option) => (
                <OptionButton 
                  key={option.id} 
                  option={option} 
                  onClick={() => handleOptionSelect(option.label, option.url, 'life')} 
                />
              ))}
            </div>
          )}

          {/* Travel Insurance - online quote component (full disclosure) + Talk to us */}
          {step === 'travel' && (
            <div className="space-y-6">
              <TravelQuoteOnline />
              <TalkToUsContent category="travel" />
            </div>
          )}

          {/* Marine Insurance Options */}
          {step === 'marine' && (
            <div className="grid gap-3">
              {marineOptions.map((option) => (
                <OptionButton 
                  key={option.id} 
                  option={option} 
                  onClick={() => handleOptionSelect(option.label, option.url, 'marine')} 
                />
              ))}
            </div>
          )}

          {/* Not Sure */}
          {step === 'notSure' && <NotSureContent />}

          {/* Talk to Us (standardized for all Talk-to-Us flows) */}
          {step === 'talkToUs' && <TalkToUsContent category={selectedCategory} />}
        </div>

        {/* Footer */}
        {step === 'categories' && (
          <div className="p-4 sm:p-6 pt-3 sm:pt-4 border-t border-neutral-100 bg-neutral-50">
            <p className="text-xs sm:text-sm text-neutral-600 text-center" >
              Need help choosing?{' '}
              <a href="tel:604-298-1218" className="text-cta hover:text-cta-hover font-medium">
                Call us at 604-298-1218
              </a>
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;
